<script lang="ts">
  import Swarm, { type SwarmStats } from '$lib/components/swarm/Swarm.svelte';
  import AzimuthLiveHero from '$lib/components/sections/AzimuthLiveHero.svelte';
  import BackedBy from '$lib/components/sections/BackedBy.svelte';
  import Contact from '$lib/components/sections/Contact.svelte';
  import { trackCtaClick } from '$lib/utils/analytics';

  // Mission: John Calabrese, "Same Models, Same Tools: The Edge Is How Much You
  // Can Let Go" (Notion, 2026-09-29). Product lines and proof carried over from
  // the previous homepage; AGIS wording follows the A.G.I.S Product Vision.

  let stats = $state<SwarmStats>({ scene: 0, kind: 'mandate', executed: 0, runs: 0, held: 0, strain: 0, cycle: 0 });

  const gap = [
    { value: '62%', label: 'of financial-services organizations already use AI agents' },
    { value: '5%', label: 'of those agent users let agents act independently on critical actions' }
  ];

  const hitl = [
    {
      title: 'It gets tired.',
      body: 'People are poor at sustained vigilance over streams that are almost always fine. By the fortieth approval of the day, the reviewer is matching the shape of the dialog box, not reading it.',
      icon: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'
    },
    {
      title: 'More approval can mean less safety.',
      body: 'Model reviewers realistically, with attention that degrades under load, and safety follows an inverted U: past a certain escalation rate, more human checks make the system less safe.',
      icon: 'M3 19C7 3 17 3 21 19M3 21h18'
    },
    {
      title: 'The gate itself is attackable.',
      body: '“Loopjacking”: what a human approved is not what runs. Tool configurations change after approval; command arguments change between approval and execution.',
      icon: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 7.5-2M12 15v2'
    }
  ];

  const intent = [
    { n: '01', name: 'You state intent', body: 'What the agent is for, what counts as success, and what must never happen. Plain language, owned by your team.' },
    { n: '02', name: 'We derive the controls', body: 'Analysis of the agent’s code and runtime maps which untrusted inputs can reach which sensitive actions, and classifies each action by blast radius.' },
    { n: '03', name: 'Traps where abuse would show', body: 'Canary credentials that should never be used, and tripwires on arguments and budgets, turn a hijacked agent into a loud signal instead of a silent breach.' },
    { n: '04', name: 'Humans see only what matters', body: 'Routine work runs. Out-of-policy behavior is blocked or escalated, with the path that led to it — and every control traces back to the policy line it came from.' }
  ];

  const proof = [
    { value: '86.3%', label: 'Detection rate on', link: '/evmbench', linkText: 'EVMBench' },
    { value: '#1', label: 'on Sherlock Audit Engine competition' },
    { value: '#1', label: "on Nethermind's AgentArena" },
    { value: '11.8M+', label: 'tokens analyzed' },
    { value: 'Billions', label: 'of dollars in critical findings autonomously identified' }
  ];

  const products = [
    {
      title: 'Secure your agents',
      name: 'AGIS',
      blurb: 'Give AI agents real authority over money — and still answer for what they do.',
      href: '/products/agis',
      bullets: ['AI agent treasury management', 'AI agent wallet security', 'Autonomous trading agents'],
      icon: 'M5 10a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3zM12 3v4M8 12h.01M16 12h.01M9 16h6'
    },
    {
      title: 'Secure your codebase',
      name: 'Azimuth',
      blurb: 'Prove exploitable paths before attackers do.',
      href: '/products/azimuth',
      bullets: ['Pre-launch protocol audits', 'Continuous smart contract monitoring', 'DeFi protocol security'],
      icon: 'm8 9-4 3 4 3M16 9l4 3-4 3M14 5l-4 14'
    },
    {
      title: 'Secure your listings',
      name: 'Token Custody',
      blurb: 'Continuously classify token behavior and risk.',
      href: '/products/token-custody',
      bullets: ['Token launch security', 'Exchange listing due diligence'],
      icon: 'M20 12a8 8 0 1 1-16 0 8 8 0 0 1 16 0zM9 9.5c0-1 1.3-1.5 3-1.5s3 .6 3 1.7-1.2 1.6-3 1.8-3 .8-3 1.8 1.3 1.7 3 1.7 3-.5 3-1.5M12 6v12'
    }
  ];
</script>

<svelte:head>
  <title>TestMachine — The edge is how much you can let go</title>
  <meta name="description" content="Same models, same tools: the edge in agentic AI goes to whoever can safely let their agents do the most. You set the intent; TestMachine turns it into guardrails, enforces them on every action, and tells you the moment an agent drifts." />
  <meta property="og:title" content="TestMachine — The edge is how much you can let go" />
  <meta property="og:description" content="Security that decides how much autonomy you can afford. Humans set intent; TestMachine derives the guardrails, enforces them, and keeps the record." />
  <meta property="og:image" content="https://testmachine.ai/testmachine-og-logo.png" />
  <meta property="og:url" content="https://testmachine.ai" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="TestMachine" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="TestMachine — The edge is how much you can let go" />
  <meta name="twitter:description" content="Security that decides how much autonomy you can afford. Humans set intent; TestMachine derives the guardrails, enforces them, and keeps the record." />
  <meta name="twitter:image" content="https://testmachine.ai/testmachine-og-logo.png" />
</svelte:head>

<Swarm onstats={(s) => (stats = s)} />

<div class="home">
  <!-- 1 · Mission -->
  <section class="scene hero" data-swarm="mandate">
    <div class="field" data-swarm-anchor aria-hidden="true"></div>
    <div class="hero-core" data-swarm-avoid>
      <p class="eyebrow">TestMachine · Security for the agentic age</p>
      <h1>Same models. Same tools. The edge is how much you can <em>let&nbsp;go</em>.</h1>
      <p class="sub">Capability is converging. What still differs is how much of it you can put to work without a person watching every step. You set the intent — TestMachine turns it into guardrails, enforces them on every action, and tells you the moment an agent drifts.</p>
      <div class="ctas">
        <a class="btn primary" href="/#contact" onclick={() => trackCtaClick('home_hero_demo', 'Book a Demo', '/#contact')}>Book a Demo</a>
        <a class="btn" href="https://app.testmachine.ai/" target="_blank" rel="noopener noreferrer" onclick={() => trackCtaClick('home_hero_launch_app', 'Community Edition', 'https://app.testmachine.ai/')}>Community Edition</a>
      </div>
    </div>
    <p class="hint">Every dot is an agent action running inside its mandate. Push one out — or click to apply pressure.</p>
  </section>

  <!-- 2 · The autonomy gap -->
  <section class="scene gap" data-swarm="text" data-swarm-text={gap.map((g) => g.value).join('|')}>
    <div class="wrap">
      <p class="eyebrow">The autonomy gap</p>
      <h2>The autonomy gap is where the value is.</h2>
      <div class="number" data-swarm-anchor aria-hidden="true"></div>
      <ul class="chips">
        {#each gap as g, i}
          <li class:on={stats.kind === 'text' && stats.scene === 1 && stats.cycle === i}><b>{g.value}</b><span>{g.label}</span></li>
        {/each}
      </ul>
      <p class="body">Every approval gate is a queue, and an agent waiting on a person is capacity you paid for, sitting idle. Autonomy does not make a model smarter. It lets you actually use the intelligence you already have.</p>
      <p class="source">Source: <a href="https://cloudsecurityalliance.org/artifacts/state-of-cloud-and-ai-for-financial-services-2026" target="_blank" rel="noopener noreferrer">Cloud Security Alliance, State of Cloud and AI for Financial Services 2026</a> (340 respondents).</p>
    </div>
  </section>

  <!-- 3 · Human-in-the-loop -->
  <section class="scene hitl" data-swarm="glyphs">
    <div class="wrap">
      <h2>Human-in-the-loop does not scale — and it is not as safe as it looks.</h2>
      <div class="three">
        {#each hitl as h}
          <article>
            <div class="glyph" data-swarm-glyph={h.icon}></div>
            <h3>{h.title}</h3>
            <p>{h.body}</p>
          </article>
        {/each}
      </div>
      <p class="kicker">None of this means humans should leave. It means humans are in the wrong place.</p>
    </div>
  </section>

  <!-- 4 · Intent -->
  <section class="scene intent" data-swarm="orbit">
    <div class="wrap grid">
      <div class="copy">
        <p class="eyebrow">How it works</p>
        <h2>Stop writing guardrails. <u>Start writing intent.</u></h2>
        <p class="lead">Humans set policy, goals and acceptable outcomes. A system turns that intent into guardrails, enforces them, and tells you the moment an agent drifts.</p>
        <ol class="steps">
          {#each intent as s}
            <li><span>{s.n}</span><div><b>{s.name}</b><p>{s.body}</p></div></li>
          {/each}
        </ol>
      </div>
      <div class="orbit" data-swarm-anchor>
        <div class="hub"><b>Your intent</b><span>policy · goals · outcomes</span></div>
        <div class="node n0"><b>Reachability</b><span>source → sink</span></div>
        <div class="node n1"><b>Blast radius</b><span>per action</span></div>
        <div class="node n2"><b>The record</b><span>every block, escalation, tripwire</span></div>
        <div class="node n3"><b>Traps</b><span>canaries & tripwires</span></div>
      </div>
    </div>
  </section>

  <!-- 5 · Where this comes from -->
  <section class="scene attacker" data-swarm="text" data-swarm-text={proof.map((p) => p.value).join('|')}>
    <div class="wrap">
      <p class="eyebrow">The foundation</p>
      <h2>We built the attacker first<span class="dim">…</span> <u>and it works</u></h2>
      <p class="body center">Azimuth, our attack agent, explores systems, reasons about attack paths and validates them through execution. That offensive understanding of how complex systems fail is how we derive controls: we know which inputs can reach which actions because finding those paths is what we do.</p>
      <div class="number" data-swarm-anchor aria-hidden="true"></div>
      <ul class="chips">
        {#each proof as m, i}
          <li class:on={stats.kind === 'text' && stats.scene === 4 && stats.cycle === i}>
            <b>{m.value}</b>
            <span>{m.label}{#if m.link}&nbsp;<a href={m.link}>{m.linkText}</a>{/if}</span>
          </li>
        {/each}
      </ul>
    </div>
  </section>

  <div class="live">
    <AzimuthLiveHero showIntro={false} />
  </div>

  <!-- 6 · Products -->
  <section class="scene products" data-swarm="glyphs">
    <div class="wrap">
      <p class="eyebrow">Products</p>
      <h2>Three places to let go, safely.</h2>
      <div class="three">
        {#each products as p}
          <a class="product" href={p.href}>
            <div class="glyph" data-swarm-glyph={p.icon}></div>
            <span class="pname">{p.name}</span>
            <strong>{p.title}</strong>
            <small>{p.blurb}</small>
            <span class="built">Built for</span>
            <ul>{#each p.bullets as b}<li>{b}</li>{/each}</ul>
          </a>
        {/each}
      </div>
      <p class="kicker">Trust remains the business model. Auditability is not a tax on autonomy — it is how autonomy gets approved.</p>
    </div>
  </section>

  <div class="solid">
    <BackedBy />
    <Contact />
  </div>

  {#if stats.kind === 'mandate'}
    <aside class="hud" aria-live="off">
      <div class="hud-row"><span class="dot"></span>Agents at work · inside mandate</div>
      <div class="hud-stats">
        <span><b>{stats.executed.toLocaleString()}</b>actions run</span>
        <span><b class="red">{String(stats.held).padStart(2, '0')}</b>held</span>
        <span><b>0</b>approvals asked</span>
      </div>
    </aside>
  {/if}
</div>

<style>
  .home { position: relative; color: var(--fg); overflow-x: clip; }
  .scene { position: relative; z-index: 1; min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: calc(var(--nav-h) + 40px) 24px 80px; }
  .wrap { width: 100%; max-width: var(--max-w); margin: 0 auto; }
  .grid { display: grid; grid-template-columns: 1.05fr 1fr; gap: clamp(32px, 6vw, 96px); align-items: center; }
  .eyebrow { font: 700 0.75rem var(--font-mono); letter-spacing: 0.16em; text-transform: uppercase; color: var(--editorial-muted); margin-bottom: 18px; }
  h1, h2, h3 { font-family: var(--font-display); letter-spacing: -0.03em; line-height: 1.05; }
  h2 { font-size: clamp(2rem, 4.4vw, 3.4rem); font-weight: 800; margin-bottom: 24px; max-width: 22ch; }
  u { text-decoration: none; background: linear-gradient(var(--accent), var(--accent)) no-repeat 0 94% / 100% 0.08em; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .dim { color: var(--fg-dim); }
  .btn { display: inline-block; font: 600 0.85rem var(--font-mono); padding: 13px 22px; border: 1px solid var(--fg); color: var(--fg); background: color-mix(in srgb, var(--bg) 80%, transparent); text-decoration: none; transition: transform 0.2s; }
  .btn:hover { transform: translateY(-2px); }
  .btn.primary { background: var(--accent); border-color: var(--accent); }
  .body { font: 1.05rem/1.65 var(--font-body); max-width: 60ch; margin-top: 28px; }
  .center { text-align: center; margin: 0 auto 8px; }
  .source { margin-top: 14px; font: 0.7rem var(--font-mono); color: var(--fg-muted); }
  .source a { color: inherit; }
  .wrap > .kicker { margin-top: 48px; font: 700 clamp(1.15rem, 2vw, 1.5rem)/1.35 var(--font-display); letter-spacing: -0.01em; max-width: 40ch; }

  /* hero */
  .field { position: absolute; left: max(24px, calc(50% - 640px)); right: max(24px, calc(50% - 640px)); top: calc(var(--nav-h) + 36px); bottom: 70px; pointer-events: none; }
  .hero-core { position: relative; max-width: 820px; text-align: center; padding: 36px 32px; }
  .hero h1 { font-size: clamp(2.4rem, 5.6vw, 4.6rem); font-weight: 800; margin-bottom: 26px; }
  .hero h1 em { font-style: normal; background: linear-gradient(var(--accent), var(--accent)) no-repeat 0 92% / 100% 0.07em; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .sub { font: clamp(1rem, 1.5vw, 1.18rem)/1.55 var(--font-body); color: var(--fg-muted); max-width: 60ch; margin: 0 auto 34px; }
  .ctas { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
  .hint { position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%); font: 0.7rem var(--font-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--fg-muted); white-space: nowrap; }

  /* gap / attacker (number scenes) */
  .gap .wrap, .attacker .wrap { text-align: center; }
  .gap h2, .attacker h2 { margin: 0 auto 12px; }
  .gap .body { margin: 28px auto 0; }
  .number { height: clamp(170px, 26vw, 300px); width: 100%; }
  .chips { list-style: none; display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
  .chips li { display: flex; gap: 10px; align-items: baseline; padding: 10px 14px; border: 1px solid var(--border); font: 0.72rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.05em; background: color-mix(in srgb, var(--bg) 88%, transparent); transition: background 0.3s, border-color 0.3s; text-align: left; max-width: 420px; }
  .chips li b { font: 800 1.15rem var(--font-display); text-transform: none; letter-spacing: -0.01em; white-space: nowrap; }
  .chips li.on { background: var(--accent); border-color: var(--accent); }
  .chips a { color: inherit; }

  /* three-up */
  .three { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; margin-top: 12px; }
  .glyph { height: clamp(140px, 18vw, 200px); margin-bottom: 18px; }
  .hitl h3 { font-size: 1.45rem; font-weight: 800; margin-bottom: 10px; }
  .hitl p { font: 0.98rem/1.6 var(--font-body); color: var(--fg-muted); }
  .product { display: flex; flex-direction: column; gap: 8px; color: var(--fg); text-decoration: none; }
  .pname { font: 700 0.7rem var(--font-mono); letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent-hover); }
  .product strong { font: 800 1.5rem var(--font-display); letter-spacing: -0.02em; }
  .product small { font: 1rem/1.5 var(--font-body); color: var(--fg-muted); }
  .built { margin-top: 14px; font: 700 0.66rem var(--font-mono); letter-spacing: 0.14em; text-transform: uppercase; color: var(--editorial-muted); }
  .product ul { list-style: none; font: 0.78rem/1.9 var(--font-mono); border-top: 1px solid var(--border); padding-top: 8px; }
  .product:hover strong { text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 6px; text-decoration-thickness: 3px; }

  /* intent */
  .copy .lead { font: 700 1.1rem/1.55 var(--font-body); margin-bottom: 24px; max-width: 52ch; }
  .steps { list-style: none; display: grid; gap: 16px; }
  .steps li { display: grid; grid-template-columns: 34px 1fr; gap: 10px; }
  .steps span { font: 700 0.75rem var(--font-mono); color: var(--accent-hover); padding-top: 3px; }
  .steps b { font: 800 1.05rem var(--font-display); }
  .steps p { font: 0.94rem/1.55 var(--font-body); color: var(--fg-muted); margin-top: 2px; }
  .orbit { position: relative; aspect-ratio: 1; width: 100%; max-width: 520px; margin: 0 auto; }
  .hub, .node { position: absolute; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 2px; font: 0.68rem var(--font-mono); background: color-mix(in srgb, var(--bg) 85%, transparent); padding: 6px 10px; }
  .hub { left: 50%; top: 50%; transform: translate(-50%, -50%); }
  .hub b, .node b { font: 800 0.98rem var(--font-display); }
  .node span, .hub span { color: var(--fg-muted); max-width: 18ch; }
  .n0 { top: 0; left: 50%; transform: translate(-50%, -30%); }
  .n1 { right: 0; top: 50%; transform: translate(30%, -50%); }
  .n2 { bottom: 0; left: 50%; transform: translate(-50%, 30%); }
  .n3 { left: 0; top: 50%; transform: translate(-30%, -50%); }

  .live { position: relative; z-index: 1; background: var(--bg); }
  .solid { position: relative; z-index: 1; background: var(--bg); }

  .hud { position: fixed; z-index: 5; right: 20px; bottom: 20px; font: 0.68rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.08em; background: var(--fg); color: var(--bg); padding: 12px 14px; pointer-events: none; }
  .hud-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); animation: pulse 1.4s infinite; }
  .hud-stats { display: flex; gap: 18px; }
  .hud-stats b { display: block; font: 800 1.5rem var(--font-display); color: var(--accent); letter-spacing: 0; font-variant-numeric: tabular-nums; }
  .hud-stats b.red { color: #ff8f93; }
  @keyframes pulse { 50% { opacity: 0.3; } }

  @media (max-width: 900px) {
    .grid, .three { grid-template-columns: 1fr; }
    .three { gap: 44px; }
    .glyph { height: 130px; }
    .hint { display: none; }
    .hud { right: 10px; bottom: 10px; padding: 8px 10px; }
    .hud-stats b { font-size: 1.05rem; }
  }
</style>
